import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useBranding } from "@/contexts/BrandingContext";
import { CompanyStep, LmsStep, AdminStep, EmailStep, PacksStep, ReviewStep } from "@/components/setup/SetupSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Company & brand", "LMS & assessment", "Admin account", "Email sender", "Starter qualifications", "Review"];
const INITIAL = {
  setup_token: "", company_name: "", short_name: "", website: "", company_email: "", brand_color: "#0F172A",
  lms_name: "", lms_short_name: "", admin_name: "", admin_email: "", admin_password: "", admin_password2: "",
  sender_email: "", sender_name: "", packs: [],
};

const validate = (step, f) => {
  if (step === 0 && !f.company_name.trim()) return "Company name is required";
  if (step === 1 && !f.lms_name.trim()) return "LMS name is required";
  if (step === 2) {
    if (!f.admin_name.trim() || !f.admin_email.trim()) return "Admin name and email are required";
    if (f.admin_password.length < 8) return "Password must be at least 8 characters";
    if (f.admin_password !== f.admin_password2) return "Passwords don't match";
  }
  return "";
};

export default function SetupPage() {
  const { refresh, setup_token_required } = useBranding();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [packs, setPacks] = useState([]);
  const [logoPreview, setLogoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { document.title = "Set up your CRM"; axios.get(`${API}/setup/status`).then(({ data }) => setPacks(data.packs || [])).catch(() => {}); }, []);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("setup_token", form.setup_token);
    try { await axios.post(`${API}/setup/logo`, fd); setLogoPreview(URL.createObjectURL(file)); toast.success("Logo uploaded"); }
    catch (err) { toast.error(err.response?.data?.detail || "Logo upload failed"); }
  };

  const next = () => { const err = validate(step, form); if (err) { toast.error(err); return; } setStep((s) => s + 1); };

  const finish = async () => {
    setBusy(true);
    try {
      const { admin_password2, ...payload } = form;
      await axios.post(`${API}/setup/complete`, payload, { withCredentials: true });
      await refresh?.();
      toast.success("All set — welcome to your new CRM");
      window.location.assign("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Setup failed");
    } finally { setBusy(false); }
  };

  const body = [
    <CompanyStep key="c" form={form} set={set} tokenRequired={setup_token_required} onLogo={onLogo} logoPreview={logoPreview} />,
    <LmsStep key="l" form={form} set={set} />,
    <AdminStep key="a" form={form} set={set} />,
    <EmailStep key="e" form={form} set={set} />,
    <PacksStep key="p" form={form} packs={packs} setPacks={(v) => set("packs", v)} />,
    <ReviewStep key="r" form={form} packs={packs} />,
  ][step];

  return (
    <div className="min-h-screen grid lg:grid-cols-[380px_1fr] bg-slate-50" data-testid="setup-page">
      <aside className="hidden lg:flex flex-col justify-between p-10 text-white" style={{ backgroundColor: form.brand_color }}>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] opacity-70">First-time setup</p>
          <h1 className="mt-3 font-heading text-4xl font-bold leading-tight">{form.company_name || "Your training company"}</h1>
          <p className="mt-2 text-sm opacity-80">CRM · {form.lms_name || "LMS"} · Assessment</p>
        </div>
        <ol className="space-y-3">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 text-sm transition-opacity ${i === step ? "opacity-100 font-semibold" : "opacity-60"}`}>
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/60 text-[11px]">{i < step ? <Check size={12} /> : i + 1}</span>{s}
            </li>
          ))}
        </ol>
      </aside>
      <main className="flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-xl border border-border bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid="setup-step-label">Step {step + 1} of {STEPS.length}</p>
          <h2 className="mt-1 mb-6 font-heading text-2xl font-bold tracking-tight">{STEPS[step]}</h2>
          {body}
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="gap-1" data-testid="setup-back"><ChevronLeft size={14} /> Back</Button>
            {step < STEPS.length - 1
              ? <Button onClick={next} className="gap-1 text-white" style={{ backgroundColor: form.brand_color }} data-testid="setup-next">Continue <ChevronRight size={14} /></Button>
              : <Button onClick={finish} disabled={busy} className="gap-1 text-white" style={{ backgroundColor: form.brand_color }} data-testid="setup-finish">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Finish setup</Button>}
          </div>
        </div>
      </main>
    </div>
  );
}
